import { api } from '../api/client';
import { store } from '../state/store';

export function initAdminControls(): void {
  // Auth modal
  const authModal = document.getElementById('auth-modal');
  const authCloseBtn = document.getElementById('auth-modal-close-btn');
  const authCancelBtn = document.getElementById('auth-cancel-btn');
  const authForm = document.getElementById('admin-login-form') as HTMLFormElement;
  const tokenInput = document.getElementById('admin-token-input') as HTMLInputElement;
  const authError = document.getElementById('auth-error');

  // Settings modal
  const settingsModal = document.getElementById('settings-modal');
  const settingsCloseBtn = document.getElementById('settings-close-btn');
  const settingsCancelBtn = document.getElementById('settings-cancel-btn');
  const settingsForm = document.getElementById('settings-form') as HTMLFormElement;

  // Add years button
  const addYearBtn = document.getElementById('add-year-btn');
  addYearBtn?.addEventListener('click', async () => {
    const newYears = store.meta.total_years + 10;
    try {
      await api.updateConfig({ total_years: newYears });
      store.meta.total_years = newYears;
      store.notify();
    } catch (err) {
      alert(`Failed to add years: ${String(err)}`);
    }
  });

  // Auth modal events
  authCloseBtn?.addEventListener('click', closeAuthModal);
  authCancelBtn?.addEventListener('click', closeAuthModal);
  authModal?.addEventListener('click', (e) => {
    if (e.target === authModal) closeAuthModal();
  });

  authForm?.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (authError) authError.classList.add('hidden');
    const token = tokenInput.value.trim();
    if (!token) return;

    try {
      const res = await api.login(token);
      if (res.success) {
        closeAuthModal();
        tokenInput.value = '';
        // Reload all data with admin privileges
        const [meta, notes] = await Promise.all([api.getMeta(), api.getWeeks()]);
        store.setMeta(meta);
        store.setNotes(notes);
      } else {
        if (authError) {
          authError.textContent = res.message || 'Invalid credentials';
          authError.classList.remove('hidden');
        }
      }
    } catch (err) {
      if (authError) {
        authError.textContent = `Error: ${String(err)}`;
        authError.classList.remove('hidden');
      }
    }
  });

  // Settings modal events
  settingsCloseBtn?.addEventListener('click', closeSettingsModal);
  settingsCancelBtn?.addEventListener('click', closeSettingsModal);
  settingsModal?.addEventListener('click', (e) => {
    if (e.target === settingsModal) closeSettingsModal();
  });

  settingsForm?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const titleInput = document.getElementById('setting-title-input') as HTMLInputElement;
    const bioInput = document.getElementById('setting-bio-input') as HTMLTextAreaElement;
    const birthdateInput = document.getElementById('setting-birthdate-input') as HTMLInputElement;
    const yearsInput = document.getElementById('setting-years-input') as HTMLInputElement;
    const passwordInput = document.getElementById('setting-password-input') as HTMLInputElement;

    const payload = {
      title: titleInput.value.trim(),
      bio: bioInput.value.trim(),
      birthdate: birthdateInput.value,
      total_years: parseInt(yearsInput.value, 10),
      admin_password: passwordInput.value.trim() || undefined,
    };

    try {
      await api.updateConfig(payload);
      const [meta, notes] = await Promise.all([api.getMeta(), api.getWeeks()]);
      store.setMeta(meta);
      store.setNotes(notes);
      closeSettingsModal();
    } catch (err) {
      alert(`Failed to save settings: ${String(err)}`);
    }
  });

  // Backup export
  const exportBtn = document.getElementById('export-backup-btn');
  exportBtn?.addEventListener('click', async () => {
    try {
      const backup = await api.exportBackup();
      const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `lifecal-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(`Export failed: ${String(err)}`);
    }
  });

  // Backup import
  const importFileInput = document.getElementById('import-backup-file') as HTMLInputElement;
  importFileInput?.addEventListener('change', async (e) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const data = JSON.parse(text);
      if (!confirm('Importing this backup will overwrite existing entries. Proceed?')) {
        importFileInput.value = '';
        return;
      }

      await api.importBackup(data);
      alert('Data restored successfully!');
      const [meta, notes] = await Promise.all([api.getMeta(), api.getWeeks()]);
      store.setMeta(meta);
      store.setNotes(notes);
      closeSettingsModal();
    } catch (err) {
      alert(`Import failed: ${String(err)}`);
    } finally {
      importFileInput.value = '';
    }
  });
}

export function renderAdminHeaderControls(): void {
  const container = document.getElementById('admin-auth-container');
  const adminElements = document.querySelectorAll('.admin-only');

  if (!container) return;
  container.innerHTML = '';

  if (store.meta.isAdmin) {
    adminElements.forEach((el) => el.classList.remove('hidden'));

    const group = document.createElement('div');
    group.style.display = 'flex';
    group.style.alignItems = 'center';
    group.style.gap = '0.5rem';

    const settingsBtn = document.createElement('button');
    settingsBtn.className = 'btn btn-outline btn-sm';
    settingsBtn.textContent = '⚙️ Settings';
    settingsBtn.addEventListener('click', openSettingsModal);

    const logoutBtn = document.createElement('button');
    logoutBtn.className = 'btn btn-outline btn-sm';
    logoutBtn.textContent = 'Logout';
    logoutBtn.addEventListener('click', async () => {
      api.setAuthToken(null);
      const [meta, notes] = await Promise.all([api.getMeta(), api.getWeeks()]);
      store.setMeta(meta);
      store.setNotes(notes);
    });

    group.appendChild(settingsBtn);
    group.appendChild(logoutBtn);
    container.appendChild(group);
  } else {
    adminElements.forEach((el) => el.classList.add('hidden'));

    const loginBtn = document.createElement('button');
    loginBtn.className = 'btn btn-outline btn-sm';
    loginBtn.textContent = 'Admin Login';
    loginBtn.addEventListener('click', openAuthModal);
    container.appendChild(loginBtn);
  }
}

export function openAuthModal(): void {
  const modal = document.getElementById('auth-modal');
  const input = document.getElementById('admin-token-input') as HTMLInputElement;
  const error = document.getElementById('auth-error');
  if (error) error.classList.add('hidden');
  if (input) input.value = '';
  if (modal) modal.classList.remove('hidden');
  input?.focus();
}

export function closeAuthModal(): void {
  const modal = document.getElementById('auth-modal');
  if (modal) modal.classList.add('hidden');
}

export function openSettingsModal(): void {
  const modal = document.getElementById('settings-modal');
  const titleInput = document.getElementById('setting-title-input') as HTMLInputElement;
  const bioInput = document.getElementById('setting-bio-input') as HTMLTextAreaElement;
  const birthdateInput = document.getElementById('setting-birthdate-input') as HTMLInputElement;
  const yearsInput = document.getElementById('setting-years-input') as HTMLInputElement;
  const passwordInput = document.getElementById('setting-password-input') as HTMLInputElement;

  if (titleInput) titleInput.value = store.meta.title;
  if (bioInput) bioInput.value = store.meta.bio;
  if (birthdateInput) birthdateInput.value = store.meta.birthdate;
  if (yearsInput) yearsInput.value = String(store.meta.total_years);
  if (passwordInput) passwordInput.value = '';

  if (modal) modal.classList.remove('hidden');
}

export function closeSettingsModal(): void {
  const modal = document.getElementById('settings-modal');
  if (modal) modal.classList.add('hidden');
}
